import type { BaseDevice } from '../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { KeyboardLightingFeature } from '../../../features/keyboard-lighting/contract'
import type { RGB, RGBGroup } from '../../../types/color'
import type {
  KeyboardLightingEffect,
  keyMapLighting,
  keyRgbItem,
} from '../../../types/keyboardLighting'
import type { KeyboardLightingTarget } from '../../../types/lighting'
import { LightingDomainType, type LightingConfigData } from '../../../types/lightingDomain'
import type { CH315KeyboardProtocol } from '../protocols'
import { CH315Memory } from '../capabilities/memory'
import { CH315_FEATURE_LAYOUT, getCH315ConfigBase } from '../types/feature-types'
import { CH315FeatureMemory } from './memory'

const CUSTOM_STATIC_BASE = 0x10016000
const REALTIME_RGB_BASE = 0x00008000

export class CH315KeyboardLighting extends KeyboardLightingFeature {
  private readonly memory: CH315FeatureMemory
  private readonly writableMemory: CH315Memory

  constructor(
    device: BaseDevice,
    private readonly protocol: CH315KeyboardProtocol,
  ) {
    super(device)
    this.memory = new CH315FeatureMemory(protocol)
    this.writableMemory = new CH315Memory(protocol)
  }

  getSupportedDomains(): LightingDomainType[] {
    return [
      LightingDomainType.Static,
      LightingDomainType.Dynamic,
      LightingDomainType.Side,
      LightingDomainType.Rgb,
    ]
  }

  async getEffects(target?: KeyboardLightingTarget): Promise<KeyboardLightingEffect[]> {
    const capability = this.device.manifest.capabilities.find((item) => item.type === 'lighting')
    const effects = (
      capability?.type === 'lighting' ? capability.config.effects : []
    ) as KeyboardLightingEffect[]
    return target ? effects.filter((effect) => effect.target === target) : [...effects]
  }

  async getCurrentEffect(): Promise<number> {
    return (await this.readConfigBytes(0x0008, 4))[0]
  }

  async getBacklightSwitch(): Promise<number> {
    return (await this.readConfigBytes(0x0004, 4))[3]
  }

  async getSideEffect(): Promise<number> {
    return (await this.readConfigBytes(0x0050, 4))[1]
  }

  async getBrightness(): Promise<number> {
    const mode = await this.getCurrentEffect()
    const config =
      mode >= 100 && mode <= 107
        ? await this.getConfig(LightingDomainType.Dynamic, mode - 100)
        : mode >= 0 && mode <= 14
          ? await this.getConfig(LightingDomainType.Static, mode)
          : undefined
    if (config?.brightness === undefined) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        `CH315 lighting mode ${mode} has no readable brightness`,
      )
    }
    return config.brightness
  }

  async getSpeed(): Promise<number> {
    const mode = await this.getCurrentEffect()
    if (mode < 100 || mode > 107) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        `CH315 lighting mode ${mode} has no speed`,
      )
    }
    return (await this.getConfig(LightingDomainType.Dynamic, mode - 100)).speed ?? 0
  }

  async getConfig(domain: LightingDomainType, index: number): Promise<LightingConfigData> {
    if (!Number.isInteger(index)) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'CH315 lighting index must be an integer',
      )
    }

    if (domain === LightingDomainType.Dynamic && index >= 0 && index < 8) {
      const bytes = await this.readConfigBytes(
        CH315_FEATURE_LAYOUT.dynamicLightingOffset + index * 8,
        8,
      )
      return decodeCH315DynamicLighting(bytes, domain, index)
    }
    if (domain === LightingDomainType.Side && index >= 0 && index < 4) {
      const bytes = await this.readConfigBytes(
        CH315_FEATURE_LAYOUT.sideLightingOffset + index * 8,
        8,
      )
      return decodeCH315DynamicLighting(bytes, domain, index)
    }
    if (domain === LightingDomainType.Static && index >= 0 && index < 15) {
      const bytes = await this.memory.read(CUSTOM_STATIC_BASE + 0x1c + index * 0x20, 8)
      return decodeCH315StaticLighting(bytes, index)
    }
    return this.unsupported(`CH315KeyboardLighting.getConfig(${domain}, ${index})`)
  }

  async getProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
  ): Promise<number | boolean | RGB | RGBGroup> {
    const config = await this.getConfig(domain, index)
    const value = config[key as keyof LightingConfigData]
    if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      (typeof value === 'object' && value !== null && !(value instanceof Uint8Array))
    ) {
      return value as number | boolean | RGB | RGBGroup
    }
    return this.unsupported(`CH315KeyboardLighting.getProperty(${key})`)
  }

  async getKeysColor(domain: LightingDomainType, index: number): Promise<keyMapLighting> {
    if (domain !== LightingDomainType.Rgb || index !== 0) {
      return this.unsupported('CH315KeyboardLighting.getKeysColor')
    }
    const bytes = await this.memory.read(REALTIME_RGB_BASE, CH315_FEATURE_LAYOUT.keyCount * 4)
    return decodeCH315KeyColors(bytes)
  }

  async *getKeysColorStream(domain: LightingDomainType, index: number): AsyncIterable<keyRgbItem> {
    for (const item of await this.getKeysColor(domain, index)) yield item
  }

  async setKeysColor(
    domain: LightingDomainType,
    keyMap: keyMapLighting,
    index: number,
  ): Promise<void[]> {
    if (domain !== LightingDomainType.Rgb || index !== 0) {
      return this.unsupported('CH315KeyboardLighting.setKeysColor')
    }
    const bytes = await this.memory.read(REALTIME_RGB_BASE, CH315_FEATURE_LAYOUT.keyCount * 4)
    for (const item of keyMap) {
      if (
        !Number.isInteger(item.index) ||
        item.index < 0 ||
        item.index >= CH315_FEATURE_LAYOUT.keyCount
      ) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 RGB key index must be 0..95')
      }
      if (!item.rgb) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 RGB key requires an rgb value')
      }
      const offset = item.index * 4
      bytes[offset] = clampByte(item.rgb.r)
      bytes[offset + 1] = clampByte(item.rgb.g)
      bytes[offset + 2] = clampByte(item.rgb.b)
    }
    await this.writableMemory.writeBulk(REALTIME_RGB_BASE, bytes, { verifyReadback: true })
    return keyMap.map(() => undefined)
  }

  async deleteCustomEffect(index: number): Promise<number> {
    if (!Number.isInteger(index) || index < 0 || index > 14) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'CH315 custom effect index must be 0..14',
      )
    }
    await this.protocol.deleteUserStaticEffect(index)
    return index
  }

  // Persistent lighting writes intentionally inherit NOT_SUPPORTED. Config/static blocks carry
  // Checksum32 fields whose update/commit semantics are not stable in the current protocol.

  private readConfigBytes(offset: number, length: number): Promise<Uint8Array> {
    return this.memory.read(getCH315ConfigBase(this.device.currentConfig.index) + offset, length)
  }
}

function clampByte(value: number): number {
  if (!Number.isFinite(value)) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 RGB component must be numeric')
  }
  return Math.min(255, Math.max(0, Math.round(value)))
}

export function decodeCH315DynamicLighting(
  bytes: Uint8Array,
  domainType: LightingDomainType.Dynamic | LightingDomainType.Side,
  index: number,
): LightingConfigData {
  if (bytes.byteLength < 8) throw new Error('CH315 dynamic lighting block is truncated')
  return {
    index,
    domainType,
    brightness: bytes[0],
    speed: bytes[1],
    isColorful: bytes[2] === 0,
    rgb: { r: bytes[4], g: bytes[5], b: bytes[6] },
    buffer: bytes.slice(0, 8),
  }
}

export function decodeCH315StaticLighting(bytes: Uint8Array, index: number): LightingConfigData {
  if (bytes.byteLength < 8) throw new Error('CH315 static lighting block is truncated')
  const type = bytes[0]
  const config: LightingConfigData = {
    index,
    domainType: LightingDomainType.Static,
    brightness: bytes[1],
    gradientDirection: type,
    buffer: bytes.slice(0, 8),
  }
  if (type === 2 || type === 3) {
    config.rgbGroup = [
      { r: bytes[2], g: bytes[3], b: bytes[4] },
      { r: bytes[5], g: bytes[6], b: bytes[7] },
    ]
  }
  return config
}

export function decodeCH315KeyColors(bytes: Uint8Array): keyMapLighting {
  if (bytes.byteLength < CH315_FEATURE_LAYOUT.keyCount * 4) {
    throw new Error('CH315 realtime RGB block is truncated')
  }
  return Array.from({ length: CH315_FEATURE_LAYOUT.keyCount }, (_, index) => ({
    index,
    rgb: {
      r: bytes[index * 4],
      g: bytes[index * 4 + 1],
      b: bytes[index * 4 + 2],
    },
  }))
}
